import { getMetricSystemByCityId } from '../../Components/WeatherWidget/util'

const apiKey = 'J42PQDKC98JVCR8NBH5NPHC5L'

const UNKNOWN_WEATHER_CONDITION = 999

const WEATHER_CONDITIONS = {
  CLEAR: 'Clear',
  PARTIALLY_CLOUDY: 'Partially cloudy',
  OVERCAST: 'Overcast',
  RAIN: 'Rain',
  RAIN_PARTIALLY_CLOUDY: 'Rain, Partially cloudy',
  RAIN_OVERCAST: 'Rain, Overcast',
  SNOW: 'Snow',
  SLEET: 'Sleet',
  FOG: 'Fog',
  FOG_PARTIALLY_CLOUDY: 'Fog, Partially cloudy',
  HAZE: 'Haze',
  MIST: 'Mist',
  DRIZZLE: 'Drizzle',
  SHOWERS: 'Showers',
  THUNDERSTORM: 'Thunderstorm',
  THUNDER_RAIN: 'Thunder-rain',
  HAIL: 'Hail',
  TORNADO: 'Tornado',
  HURRICANE: 'Hurricane',
  DUST: 'Dust',
  SMOKE: 'Smoke',
  WIND: 'Wind',
  SNOW_OVERCAST: 'Snow, Overcast',
  SNOW_PARTIALLY_CLOUDY: 'Snow, Partially cloudy',
  SNOW_RAIN_OVERCAST: 'Snow, Rain, Overcast',
}

const WEATHER_TO_MAP = {
  [WEATHER_CONDITIONS.CLEAR]: 1,
  [WEATHER_CONDITIONS.PARTIALLY_CLOUDY]: 2,
  [WEATHER_CONDITIONS.OVERCAST]: 3,
  [WEATHER_CONDITIONS.RAIN]: 4,
  [WEATHER_CONDITIONS.RAIN_PARTIALLY_CLOUDY]: 5,
  [WEATHER_CONDITIONS.RAIN_OVERCAST]: 6,
  [WEATHER_CONDITIONS.SNOW]: 7,
  [WEATHER_CONDITIONS.SLEET]: 8,
  [WEATHER_CONDITIONS.FOG]: 9,
  [WEATHER_CONDITIONS.FOG_PARTIALLY_CLOUDY]: 10,
  [WEATHER_CONDITIONS.HAZE]: 11,
  [WEATHER_CONDITIONS.MIST]: 12,
  [WEATHER_CONDITIONS.DRIZZLE]: 13,
  [WEATHER_CONDITIONS.SHOWERS]: 14,
  [WEATHER_CONDITIONS.THUNDERSTORM]: 15,
  [WEATHER_CONDITIONS.THUNDER_RAIN]: 16,
  [WEATHER_CONDITIONS.HAIL]: 17,
  [WEATHER_CONDITIONS.TORNADO]: 18,
  [WEATHER_CONDITIONS.HURRICANE]: 19,
  [WEATHER_CONDITIONS.DUST]: 20,
  [WEATHER_CONDITIONS.SMOKE]: 21,
  [WEATHER_CONDITIONS.WIND]: 22,
}

const HOURS_24 = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
]

const DANGEROUS_WEATHER_CONDITIONS = [
  WEATHER_CONDITIONS.THUNDERSTORM,
  WEATHER_CONDITIONS.THUNDER_RAIN,
  WEATHER_CONDITIONS.HAIL,
  WEATHER_CONDITIONS.TORNADO,
  WEATHER_CONDITIONS.HURRICANE,
]

const getWeatherForLocationInRange = async ({
  latitude,
  longitude,
  from,
  to,
  cityId,
}) => {
  const metric = getMetricSystemByCityId(cityId)

  const url = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${latitude},${longitude}/${from}/${to}?unitGroup=${metric}&include=hours&elements=datetime,conditions,icon&key=${apiKey}&contentType=json`

  const response = await fetch(url)
  const contentType =
    (response.headers &&
      response.headers.get &&
      response.headers.get('content-type')) ||
    ''

  if (!response.ok) {
    // Swallow details to avoid noisy console logs; caller will retry/back off
    try {
      await response.text()
    } catch (_) {}
    return { days: [] }
  }

  if (contentType.includes('application/json')) {
    try {
      const data = await response.json()
      return data
    } catch (e) {
      console.warn(
        'Weather API returned invalid JSON, falling back to empty days:',
        e && e.message,
      )
      return { days: [] }
    }
  }

  try {
    const text = await response.text()
    try {
      return JSON.parse(text)
    } catch (_) {
      return { days: [] }
    }
  } catch (_) {
    return { days: [] }
  }
}

const getWeatherForPostcodeInRange = async ({
  postcode,
  countryCode,
  from,
  to,
  cityId,
}) => {
  const metric = getMetricSystemByCityId(cityId)

  const location = countryCode ? `${postcode},${countryCode}` : postcode
  const url = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${location}/${from}/${to}?unitGroup=${metric}&include=hours&elements=datetime,conditions&key=${apiKey}&contentType=json`

  const response = await fetch(url)
  const contentType =
    (response.headers &&
      response.headers.get &&
      response.headers.get('content-type')) ||
    ''

  if (!response.ok) {
    try {
      await response.text()
    } catch (_) {}
    return { days: [] }
  }

  if (contentType.includes('application/json')) {
    try {
      const data = await response.json()
      return data
    } catch (e) {
      console.warn(
        'Weather API invalid JSON (postcode), falling back to empty days:',
        e && e.message,
      )
      return { days: [] }
    }
  }

  try {
    const text = await response.text()
    try {
      return JSON.parse(text)
    } catch (_) {
      return { days: [] }
    }
  } catch (_) {
    return { days: [] }
  }
}

export const getDatesWithDangerousWeather = data => {
  const days = data.days.filter(day =>
    DANGEROUS_WEATHER_CONDITIONS.some(item =>
      day.conditions.toUpperCase().includes(item.toUpperCase()),
    ),
  )
  return days.map(day => day.datetime)
}

const filterHoursByStartTime = (hours, morningStart) => {
  if (!morningStart) return hours

  const [startHour, startMinute = '00'] = morningStart.split(':')
  const filteredHours = {}

  for (const [hour, weatherCode] of Object.entries(hours)) {
    const [hourNum, minuteNum] = hour.split(':')
    if (
      parseInt(hourNum, 10) > parseInt(startHour, 10) ||
      (parseInt(hourNum, 10) === parseInt(startHour, 10) &&
        parseInt(minuteNum, 10) >= parseInt(startMinute, 10))
    ) {
      filteredHours[hour] = weatherCode
    }
  }

  return filteredHours
}

const processWeatherData = (data, morningStart) => {
  const result = {}

  if (!data.days || !data.days.length) return result

  for (const dayData of data.days) {
    const date = dayData.datetime
    let hours = {}

    if (dayData.hours && dayData.hours.length) {
      hours = dayData.hours.reduce((acc, item) => {
        const [hour, minute] = item.datetime.split(':')
        const formattedHour = `${hour}:${minute}`
        acc[formattedHour] =
          WEATHER_TO_MAP[item.conditions] || UNKNOWN_WEATHER_CONDITION
        return acc
      }, {})
    } else {
      const weatherCode =
        WEATHER_TO_MAP[dayData.conditions] || UNKNOWN_WEATHER_CONDITION
      hours = HOURS_24.reduce((acc, hour) => {
        acc[hour] = weatherCode
        return acc
      }, {})
    }

    result[date] = filterHoursByStartTime(hours, morningStart)
  }

  return result
}

const getWeatherForPostcodeByDates = async ({
  postcode,
  ranges,
  cityId,
  morningStart,
  countryCode,
}) => {
  const result = {}

  try {
    for (const range of ranges) {
      const data = await getWeatherForPostcodeInRange({
        postcode,
        countryCode,
        from: range.from,
        to: range.to,
        cityId,
      })

      if (data.errorCode) {
        console.error('Weather API error:', data.message)
        return null
      }

      const rangeResult = processWeatherData(data, morningStart)
      Object.assign(result, rangeResult)
    }
  } catch (error) {
    console.error('Error fetching weather for postcode:', error)
    return {}
  }

  return result
}

const getWeatherByCoordinatesByDates = async ({
  latitude,
  longitude,
  cityId,
  ranges,
  morningStart,
}) => {
  const result = {}

  try {
    for (const range of ranges) {
      const data = await getWeatherForLocationInRange({
        latitude,
        longitude,
        from: range.from,
        to: range.to,
        cityId,
      })

      if (data.errorCode) {
        console.error('Weather API error:', data.message)
        return null
      }

      const rangeResult = processWeatherData(data, morningStart)
      Object.assign(result, rangeResult)
    }
  } catch (error) {
    console.error('Error fetching weather for coordinates:', error)
    return {}
  }

  return result
}

export {
  getWeatherForLocationInRange,
  getWeatherForPostcodeInRange,
  DANGEROUS_WEATHER_CONDITIONS,
  getWeatherForPostcodeByDates,
  getWeatherByCoordinatesByDates,
  WEATHER_CONDITIONS,
  HOURS_24,
}
